import { type UseQueryResult, useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { useConfig } from 'wagmi'

import { SuspenseQueryWith } from '@/utils/types'

import { QUERY_STALE_TIME } from '@/config/consts'
import { useAccount } from '@/domain/hooks/useAccount'
import { useMemo } from 'react'
import { aaveDataLayer } from './aave-data-layer/query'
import { MarketInfo, marketInfoSelectFn } from './marketInfo'
export interface UseMarketInfoParams {
  chainId: number
  timeAdvance?: number
  refetchInterval?: number
}
export type UseMarketInfoResultOnSuccess = SuspenseQueryWith<{
  marketInfo: MarketInfo
}>

export type UseMarketInfoQueryResult = Omit<UseQueryResult<MarketInfo>, 'data'> & {
  marketInfo: MarketInfo | undefined
}

export function useMarketInfo({
  chainId,
  timeAdvance,
  refetchInterval,
}: UseMarketInfoParams): UseMarketInfoResultOnSuccess {
  const account = useAccount()
  const wagmiConfig = useConfig()

  const res = useSuspenseQuery({
    ...aaveDataLayer({
      wagmiConfig,
      chainId,
      account,
      refetchEnabled: !!refetchInterval,
    }),
    select: useMemo(() => marketInfoSelectFn({ timeAdvance }), [timeAdvance]),
    refetchInterval,
    staleTime: QUERY_STALE_TIME,
  })

  return {
    ...res,
    marketInfo: res.data,
  }
}

export function useMarketInfoQuery({
  chainId,
  timeAdvance,
  refetchInterval,
}: UseMarketInfoParams): UseMarketInfoQueryResult {
  const account = useAccount()
  const wagmiConfig = useConfig()

  const res = useQuery({
    ...aaveDataLayer({
      wagmiConfig,
      chainId,
      account,
      refetchEnabled: !!refetchInterval,
    }),
    select: useMemo(() => marketInfoSelectFn({ timeAdvance }), [timeAdvance]),
    refetchInterval,
    staleTime: QUERY_STALE_TIME,
  })

  return {
    ...res,
    marketInfo: res.data,
  }
}
